import { useEffect, useRef, useState } from "react"
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Typography,
} from "@mui/material"
import { RotateCcw } from "lucide-react"

interface SpeakerPhotoCropModalProps {
  open: boolean
  photoUrl: string
  initialPosition: string
  speakerName?: string
  onCancel: () => void
  onApply: (objectPosition: string) => void
}

const PREVIEW_SIZE = 280

const parsePosition = (value: string | undefined): { x: number; y: number } => {
  if (!value) return { x: 50, y: 50 }
  const parts = value.trim().split(/\s+/)
  const parseOne = (s: string | undefined, fallback: number): number => {
    if (!s) return fallback
    const n = parseFloat(s)
    return Number.isFinite(n) ? Math.min(100, Math.max(0, n)) : fallback
  }
  return { x: parseOne(parts[0], 50), y: parseOne(parts[1], 50) }
}

export function SpeakerPhotoCropModal({
  open,
  photoUrl,
  initialPosition,
  speakerName,
  onCancel,
  onApply,
}: SpeakerPhotoCropModalProps) {
  const [pos, setPos] = useState(() => parsePosition(initialPosition))
  const dragRef = useRef<{ x: number; y: number; startPosX: number; startPosY: number } | null>(null)

  // Reset internal position whenever the modal opens with a (possibly different) initial position
  useEffect(() => {
    if (open) setPos(parsePosition(initialPosition))
  }, [open, initialPosition])

  useEffect(() => {
    if (!open) return
    const handleMove = (e: MouseEvent) => {
      const d = dragRef.current
      if (!d) return
      const dx = e.clientX - d.x
      const dy = e.clientY - d.y
      const dxPct = (dx / PREVIEW_SIZE) * 100
      const dyPct = (dy / PREVIEW_SIZE) * 100
      // Drag right/down ⇒ reveal left/top of image ⇒ object-position decreases
      const newX = Math.min(100, Math.max(0, d.startPosX - dxPct))
      const newY = Math.min(100, Math.max(0, d.startPosY - dyPct))
      setPos({ x: newX, y: newY })
    }
    const handleUp = () => { dragRef.current = null }
    window.addEventListener("mousemove", handleMove)
    window.addEventListener("mouseup", handleUp)
    window.addEventListener("mouseleave", handleUp)
    return () => {
      window.removeEventListener("mousemove", handleMove)
      window.removeEventListener("mouseup", handleUp)
      window.removeEventListener("mouseleave", handleUp)
    }
  }, [open])

  const handleMouseDown = (e: React.MouseEvent) => {
    e.preventDefault()
    dragRef.current = {
      x: e.clientX,
      y: e.clientY,
      startPosX: pos.x,
      startPosY: pos.y,
    }
  }

  const reset = () => setPos({ x: 50, y: 50 })
  const apply = () => onApply(`${pos.x.toFixed(1)}% ${pos.y.toFixed(1)}%`)

  const objectPos = `${pos.x}% ${pos.y}%`

  const handleClose = (_e: object, reason: "backdropClick" | "escapeKeyDown") => {
    if (reason === "backdropClick" || reason === "escapeKeyDown") onCancel()
  }

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="xs">
      <DialogTitle>Adjust photo crop</DialogTitle>
      <DialogContent>
        <DialogContentText>
          Drag the photo inside the circle to position {speakerName || "the speaker"}&rsquo;s face.
          This is exactly how it will appear in the email.
        </DialogContentText>

        <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 2, py: 1 }}>
          {/* Circular crop preview — what the recipient will see */}
          <Box
            role="img"
            aria-label="Photo crop preview. Drag to reposition."
            onMouseDown={handleMouseDown}
            onDoubleClick={reset}
            sx={{
              borderRadius: "50%",
              border: 4,
              borderColor: "background.paper",
              boxShadow: 4,
              userSelect: "none",
              outline: "1px solid",
              outlineColor: "divider",
              width: PREVIEW_SIZE,
              height: PREVIEW_SIZE,
              backgroundImage: `url(${photoUrl})`,
              backgroundSize: "cover",
              backgroundPosition: objectPos,
              backgroundRepeat: "no-repeat",
              cursor: dragRef.current ? "grabbing" : "grab",
            }}
          />

          <Typography variant="caption" sx={{ color: "text.secondary", textAlign: "center" }}>
            Drag to reposition · double-click to recenter
          </Typography>
        </Box>
      </DialogContent>

      <DialogActions sx={{ justifyContent: "space-between" }}>
        <Button color="inherit" onClick={reset} startIcon={<RotateCcw size={16} />}>
          Reset
        </Button>
        <Box sx={{ display: "flex", gap: 1 }}>
          <Button variant="outlined" color="inherit" onClick={onCancel}>
            Cancel
          </Button>
          <Button variant="contained" onClick={apply}>
            Apply
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  )
}
